import { useEffect } from 'react';
import { useModals, ModalNames } from 'components/modals';
import { setPersistedValue } from 'shared/utils/persisted-values';
import { ORDER_STATUSES } from 'src/constants';
import { ConsumerUser, ConsumerOrder } from '../../account.types';
import { Orders } from './use-orders-for-consumer/types';

const { OPEN, CONFIRMED, IN_TRANSIT } = ORDER_STATUSES;
const VIEWABLE_STATUSES = new Set([OPEN, CONFIRMED, IN_TRANSIT]);

type UseOrderListParams = {
  loading: boolean;
  orders: Orders;
  user: ConsumerUser;
};

type UseOrderListReturn = {
  viewableStatuses: Set<string>;
  showLoadingSpinner: boolean;
  handleViewOrder: (order: ConsumerOrder, canViewStatus: boolean) => void;
};

export function useOrderList({ loading, orders, user }: UseOrderListParams): UseOrderListReturn {
  const { openModal } = useModals();
  const showLoadingSpinner = loading || !orders;
  const deepLinkOrderId = new URLSearchParams(window.location.search).get(`orderId`);

  // Deep-link case only: a user lands on this page with `?orderId=ABCD` (notification,
  // bookmark, etc.). Open the modal and clean the URL via the History API so we don't
  // fire Next's routeChangeStart (which the modal observer hooks to close modals).
  // Mirrors after-submit.js: persist viewOrderId to sessionStorage so PBB-return flows
  // (useFinalizePbbAfterSignup, useReopenOrderConfirmationOnPbbReturn) can read it after
  // a Plaid OAuth hard reload.
  useEffect(() => {
    if (deepLinkOrderId) {
      user.viewOrderId = deepLinkOrderId;
      setPersistedValue('viewOrderId', deepLinkOrderId, true);
      void openModal(ModalNames.orderStatus);
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [deepLinkOrderId, user, openModal]);

  const handleViewOrder = (order: ConsumerOrder, canViewStatus: boolean): void => {
    user.viewOrderId = order.orderId;
    if (typeof order.orderId === 'string') {
      setPersistedValue('viewOrderId', order.orderId, true);
    }
    if (canViewStatus && typeof order.orderId === 'string') {
      void openModal(ModalNames.orderStatus);
    } else {
      void openModal(ModalNames.orderReceipt);
    }
  };

  return {
    viewableStatuses: VIEWABLE_STATUSES,
    showLoadingSpinner,
    handleViewOrder,
  };
}
